<script lang="ts">
    import type {Document, Fond, RecordSearchParams} from 'typings/portaro.be.types';
    import type {TabButton, TabId} from 'shared/ui-widgets/tabset/types';
    import type {RecordRow} from 'src/features/record-grid/lib/types';
    import type {SearchManager} from 'src/features/search/search-manager/search-manager';
    import {DateTime} from 'luxon';
    import {Kind, SearchType} from 'shared/constants/portaro.constants';
    import {RecordGridService} from 'src/features/record-grid/services/record-grid.service';
    import {getDateFormatter, getInjector} from 'core/svelte-context/context';
    import {onMount} from 'svelte';
    import {pipe} from 'core/utils';
    import {popInAnim, popOutAnim} from 'shared/animations/pop-animations';
    import {createSutorReportsOptimisationContext} from './lib/sutor-reports-optimisation-context';
    import {SearchManagerBuilderFactoryService} from 'src/features/search/search-manager/search-manager-factory.service';
    import {SutorReportsOptimisationService} from './lib/sutor-reports-optimisation.service';
    import {exists} from 'shared/utils/custom-utils';
    import {get} from 'svelte/store';
    import {SUTOR_REPORTS_OPTIMISATION_SETTINGS} from 'src/features/sutor/sutor-constants';
    import SutorTabbedSubpagesContainer from '../../../../components/tabbed-subpages/SutorTabbedSubpagesContainer.svelte';
    import ReportOptimisationGrid from 'src/features/sutor/pages/record-detail/tab-pages/reports-optimisation-tab/grid/ReportOptimisationGrid.svelte';
    import ReportOptimisationUsers from './users/ReportOptimisationUsers.svelte';
    import KpLoadingBlock from 'shared/components/kp-loading/KpLoadingBlock.svelte';
    import KpSearchToolbar from 'src/features/search/kp-search-toolbar/KpSearchToolbar.svelte';
    import DateSelectorCalendar from './DateSelectorCalendar.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import ReportOptimisationOverviews from 'src/features/sutor/pages/record-detail/tab-pages/reports-optimisation-tab/overviews/ReportOptimisationOverviews.svelte';
    import KpHeading from 'shared/components/kp-heading/KpHeading.svelte';

    export let record: Document;

    const recordGridService = getInjector().getByClass(RecordGridService);
    const service = getInjector().getByClass(SutorReportsOptimisationService);
    const searchManagerBuilderFactoryService = getInjector().getByClass(SearchManagerBuilderFactoryService);
    const context = createSutorReportsOptimisationContext(record, service);
    const currentOptimisationDate$ = context.currentDate;
    const dateFormatter = getDateFormatter();

    let loading = true;

    const tabs = {};
    const tabButtons: TabButton[] = [
        {
            id: 'tab-grid',
            label: 'Náklady k optimalizaci',
            icon: 'list',
            tabPageWithoutPadding: true,
            tabPageContainerClass: 'record-grid-tab-page'
        },
        {
            id: 'tab-users',
            label: 'Dostupní uživatelé',
            icon: 'users',
            tabPageWithoutPadding: true
        },
        {
            id: 'tab-overviews',
            label: 'Přehledy',
            icon: 'info',
            tabPageWithoutPadding: true
        }
    ];

    onMount(async () => {
        const optimisationFond = await recordGridService.getFond(SUTOR_REPORTS_OPTIMISATION_SETTINGS.fond);
        const userListFond = await recordGridService.getFond(SUTOR_REPORTS_OPTIMISATION_SETTINGS.userListFond);

        tabs['tab-grid'] = {
            component: ReportOptimisationGrid,
            props: {
                fond: optimisationFond
            }
        };
        tabs['tab-users'] = {component: ReportOptimisationUsers};
        tabs['tab-overviews'] = {component: ReportOptimisationOverviews};

        createSearchManagers(optimisationFond, userListFond);

        loading = false;
    });

    function getToolbarSearchContext(tabId: TabId): SearchManager<RecordRow> {
        if (tabId === 'tab-grid') {
            return context.optimisationTableSearchContext;
        }

        return context.usersListSearchContext;
    }

    function createSearchManagers(optimisationFond: Fond, userListFond: Fond) {
        const optimisationTableSearchManagerBuilder = searchManagerBuilderFactoryService.createBuilder<RecordSearchParams, any>().withStaticParams(createOptimisationTableStaticParams(optimisationFond));
        context.optimisationTableSearchContext = optimisationTableSearchManagerBuilder.createLocalSearch();

        const usersListSearchManagerBuilder = searchManagerBuilderFactoryService.createBuilder<RecordSearchParams, any>().withStaticParams(createUsersListStaticParams(userListFond));
        context.usersListSearchContext = usersListSearchManagerBuilder.createLocalSearch();
    }

    function createOptimisationTableStaticParams(fond: Fond): RecordSearchParams {
        const currentDate = get(context.currentDate) ?? DateTime.now();

        return {
            qt: `{"and": [{"field": "reportDate", "eq": {"value": "${currentDate.toISODate()}"}}, {"field": "recordRelatedRecord", "eq": {"value": "${context.projectRecord.id}"}}]}`,
            kind: [Kind.KIND_RECORD],
            type: SearchType.TYPE_TABLE,
            rootFond: [fond],
            pageSize: 100
        };
    }

    function createUsersListStaticParams(fond: Fond): RecordSearchParams {
        const currentDate = get(context.currentDate) ?? DateTime.now();

        return {
            qt: `{"and": [{"not": {"field": "recordRelatedRecord", "eq": {"value": "${context.projectRecord.id}"}}}, {"field": "reportDate", "eq": {"value": "${currentDate.toISODate()}"}}]}`,
            kind: [Kind.KIND_RECORD],
            type: SearchType.TYPE_TABLE,
            rootFond: [fond],
            pageSize: 100
        };
    }

    const handleDateSelected = (event: CustomEvent<DateTime>) => {
        context.optimisationTableSearchContext.newSearchWithPartialParams({
            qt: `{"and": [{"field": "reportDate", "eq": {"value": "${event.detail.toISODate()}"}}, {"field": "recordRelatedRecord", "eq": {"value": "${context.projectRecord.id}"}}]}`
        });
        context.usersListSearchContext.newSearchWithPartialParams({
            qt: `{"and": [{"not": {"field": "recordRelatedRecord", "eq": {"value": "${context.projectRecord.id}"}}}, {"field": "reportDate", "eq": {"value": "${event.detail.toISODate()}"}}]}`
        });

        context.setCurrentDate(event.detail);
    };
</script>

{#if loading}
    <div class="loading-container">
        <KpLoadingBlock size="md"/>
    </div>
{/if}

<div class="overlay-container">
    {#if !loading}
        {#if !exists($currentOptimisationDate$)}
            <div class="date-select-container"
                 in:popInAnim={{key: 'date-select'}}
                 out:popOutAnim={{key: 'date-select'}}>

                <div class="heading-container">
                    <KpHeading type="h2">Vyberte datum optimalizace</KpHeading>
                    <span class="text-muted">Kliknutím na daný den se zobrazí detail optimalizace.</span>
                </div>

                <div class="calendar-container">
                    <DateSelectorCalendar on:date-selected={handleDateSelected}/>
                </div>
            </div>
        {:else}
            <div class="animation-container"
                 in:popInAnim={{key: 'tabbed'}}
                 out:popOutAnim={{key: 'tabbed'}}>

                <SutorTabbedSubpagesContainer {tabButtons}
                                              let:activeTab
                                              additionalClasses="reports-optimisation-tabbed-subpages"
                                              withoutUrlManagement>

                    <svelte:fragment slot="additional-content" let:activeTab={currentActiveTab}>
                        <button class="date-change-button" on:click={() => context.setCurrentDate(null)}>
                            <IconedContent icon="calendar">
                                {pipe($currentOptimisationDate$.toISODate(), dateFormatter('d. M. yyyy'))}
                            </IconedContent>

                            <IconedContent icon="arrow-small-left">Zvolit jiný datum</IconedContent>
                        </button>

                        {#key currentActiveTab}
                            <KpSearchToolbar injectedSearchManager="{getToolbarSearchContext(currentActiveTab)}"/>
                        {/key}
                    </svelte:fragment>

                    <svelte:component this="{tabs[activeTab].component}" {...tabs[activeTab].props ?? {}}/>
                </SutorTabbedSubpagesContainer>
            </div>
        {/if}
    {/if}
</div>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";
    @import (reference) "styles/portaro-erp.less";

    .loading-container {
        .flex-grow();
        align-items: center;
        justify-content: center;
    }

    .overlay-container {
        .flex-grow();
        position: relative;
    }

    .date-select-container,
    .animation-container {
        .flex-grow();
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
    }

    .date-select-container {
        gap: @spacing-l;
        padding: @spacing-xl @main-padding-horizontal @main-padding-vertical;

        .heading-container {
            display: flex;
            flex-direction: column;
            gap: @spacing-s;

            span {
                font-size: @font-size-small;
            }
        }

        .calendar-container {
            .flex-grow();
        }
    }

    .animation-container .date-change-button {
        outline: none;
        background: none;
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        white-space: nowrap;
        gap: @spacing-sm;
        color: @themed-text-muted;
        font-size: @font-size-small;
        padding: @spacing-s @spacing-sm;
        border-radius: @border-radius-default;

        &:hover {
            background-color: @themed-panel-bg;
        }
    }

    :global {
        .reports-optimisation-tabbed-subpages {
            .record-grid-tab-page .record-grid-container {
                border-top: none !important;
            }

            .date-change-button .uicon {
                font-size: @font-size-default;
            }
        }
    }
</style>